'use client';
import env from '@trashlab/sitesapp/src/env';
import {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Box, Text, Card, Flex } from '@radix-ui/themes';
import { IconCreditCard, IconAlertCircle } from '@tabler/icons-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useImmer } from 'use-immer';
import { PaymentMethodCreateParamsType } from 'tilled-node';
import { sanitizePaymentServiceErrorMessage } from '@trashlab/api/src/library/frontend/utils/payment-service';
import { useCreateCheckoutPaymentMethod } from '@trashlab/sitesapp/src/components/payments/use-create-checkout-payment-method';
import { FormInput } from '@trashlab/sitesapp/src/templates/default/components/shared/form-input';

// Minimal typings for Tilled SDK objects we interact with
interface TilledField {
  inject: (selector: string) => TilledField;
  on: (event: 'change', handler: (event: TilledFieldEvent) => void) => void;
}

interface TilledForm {
  createField: (
    field: 'cardNumber' | 'cardExpiry' | 'cardCvv',
    options: { styles: unknown },
  ) => TilledField;
  build: () => void;
  teardown: () => void;
}

interface TilledClient {
  form: (options: { payment_method_type: 'card' }) => Promise<TilledForm>;
  createPaymentMethod: (params: {
    type: 'card';
    billing_details: {
      name: string;
      address: { zip: string; country: string };
    };
  }) => Promise<{ id: string }>;
}

type TilledConstructor = new (
  publishableKey: string,
  payfacId: string,
  options: { sandbox: boolean },
) => TilledClient;

declare const Tilled: unknown;

interface Props {
  cartId: string;
  organization: {
    id: string;
    isTestOrg: boolean;
    payfacId: string | null;
  };
  allowedPaymentMethodTypes: PaymentMethodCreateParamsType[];
  onValidationChange?: (isValid: boolean) => void;
  isInvalid?: boolean;
  onSave?: (paymentMethod: {
    id: string;
    billingDetails: { name: string; postalCode: string };
  }) => Promise<void>;
}

const formSchema = z.object({
  name: z.string().min(2, 'Name must be between 2 and 22 characters long.'),
  postalCode: z.string().min(5, 'Postal code must be at least 5 characters'),
});

type FormValues = z.infer<typeof formSchema>;

type TilledFieldEvent = {
  valid: boolean;
  empty: boolean;
  fieldType: 'cardNumber' | 'cardExpiry' | 'cardCvv';
};

export interface PaymentFormHandle {
  submit: () => Promise<{
    paymentMethodId: string;
    billingDetails: {
      name: string;
      postalCode: string;
    };
  }>;
}

// Guard component shown when payfacId is not configured
const PaymentFormUnavailable = () => (
  <Card
    style={{
      padding: '16px',
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
    }}
  >
    <Flex align="center" gap="2">
      <IconAlertCircle size={20} color="#dc2626" />
      <Text size="2" style={{ color: '#dc2626' }}>
        Online payments are not yet configured. Please contact us to complete
        your order.
      </Text>
    </Flex>
  </Card>
);

const PaymentFormInner = forwardRef<
  PaymentFormHandle,
  Props & { organization: Props['organization'] & { payfacId: string } }
>(
  (
    {
      organization,
      cartId,
      allowedPaymentMethodTypes = ['card'],
      onValidationChange,
      isInvalid,
      onSave,
    },
    ref,
  ) => {
    const tilled = useRef<TilledClient | null>(null);
    const tilledForm = useRef<TilledForm | null>(null);
    const [paymentError, setPaymentError] = useState('');

    const [ccValidation, updateCcValidation] = useImmer({
      cardNumber: false,
      cardExpiry: false,
      cardCvv: false,
    });

    const form = useForm<FormValues>({
      resolver: zodResolver(formSchema),
      mode: 'onChange',
      defaultValues: {
        name: '',
        postalCode: '',
      },
    });

    useImperativeHandle(ref, () => ({
      submit: async () => {
        const values = form.getValues();
        return createPaymentMethod(values);
      },
    }));

    const updateField = (event: TilledFieldEvent) => {
      updateCcValidation((draft) => {
        draft[event.fieldType] = event.valid;
      });
    };

    useEffect(() => {
      const allFieldsValid = Object.values(ccValidation).every(Boolean);
      const formValid = form.formState.isValid;
      onValidationChange?.(allFieldsValid && formValid);
    }, [ccValidation, form.formState.isValid, onValidationChange]);

    const createCheckoutPaymentMethod = useCreateCheckoutPaymentMethod({
      cartId,
      tenantId: organization.id,
      onError: (message) => {
        form.setError('name', {
          message: sanitizePaymentServiceErrorMessage(message),
        });
      },
    });

    const fieldOptions = {
      styles: {
        base: {
          fontFamily: 'Helvetica Neue, Arial, sans-serif',
          color: '#304166',
          fontWeight: '400',
          fontSize: '16px',
        },
        invalid: {
          ':hover': {
            textDecoration: 'underline dotted red',
          },
        },
        valid: {
          color: '#00BDA5',
        },
      },
    };

    const cardNumberOptions = {
      ...fieldOptions,
      placeholder: '1234 1234 1234 1234',
    };

    const cardExpiryOptions = {
      ...fieldOptions,
      placeholder: 'MM/YY',
    };

    const cardCvvOptions = {
      ...fieldOptions,
      placeholder: 'CVC',
    };

    useEffect(() => {
      // initialize the tilled client
      if (Tilled !== undefined && !tilled.current) {
        console.log('initializing tilled');

        const useSandbox =
          organization.isTestOrg ||
          env.NEXT_PUBLIC_TRASHLAB_ENV !== 'production';

        const TilledCtor = Tilled as TilledConstructor;
        const client = new TilledCtor(
          useSandbox
            ? env.NEXT_PUBLIC_TILLED_SANDBOX_PUBLISHABLE_KEY
            : env.NEXT_PUBLIC_TILLED_PUBLISHABLE_KEY,
          organization.payfacId ?? '',
          { sandbox: useSandbox },
        );
        tilled.current = client;

        client
          .form({ payment_method_type: 'card' })
          .then((form: TilledForm) => {
            tilledForm.current = form;

            tilledForm.current
              .createField('cardNumber', cardNumberOptions)
              .inject('#card-number-element')
              .on('change', updateField);
            tilledForm.current
              .createField('cardExpiry', cardExpiryOptions)
              .inject('#card-expiry-element')
              .on('change', updateField);
            tilledForm.current
              .createField('cardCvv', cardCvvOptions)
              .inject('#card-cvv-element')
              .on('change', updateField);

            tilledForm.current.build();
          })
          .catch((error: unknown) => {
            console.error('Failed to initialize Tilled:', error);
            setPaymentError(
              'Failed to load payment processing. Please try again later.',
            );
          });
      }

      return () => {
        if (tilledForm.current) {
          tilledForm.current.teardown();
        }
      };
    }, [organization.isTestOrg, organization.payfacId]);

    const createPaymentMethod = async (values: FormValues) => {
      const allFieldsValid = Object.values(ccValidation).every(Boolean);
      if (!allFieldsValid) {
        throw new Error(
          'Please enter valid card information before proceeding with payment.',
        );
      }

      const params = {
        type: 'card',
        billing_details: {
          name: values.name,
          address: {
            zip: values.postalCode,
            country: 'US',
          },
        },
      } as const;

      try {
        if (!tilled.current) {
          throw new Error(
            'Payment processor not initialized. Please try again.',
          );
        }
        const tilledPaymentMethod =
          await tilled.current.createPaymentMethod(params);

        const paymentMethod = await createCheckoutPaymentMethod(
          tilledPaymentMethod.id,
        );

        return {
          paymentMethodId: paymentMethod.id,
          billingDetails: {
            name: values.name,
            postalCode: values.postalCode,
          },
        };
      } catch (error) {
        throw new Error(
          sanitizePaymentServiceErrorMessage(
            error instanceof Error ? error.message : error,
            'There was an error processing your payment. Please try again.',
          ),
        );
      }
    };

    if (!allowedPaymentMethodTypes.includes('card')) {
      return (
        <Text>
          This payment method is not available. Please contact support.
        </Text>
      );
    }

    return (
      <Box>
        <div>
          <Flex direction="column" gap="3">
            <FormInput
              {...form.register('name')}
              name="name"
              label="Name on card"
              placeholder="Name on card"
              required
              error={form.formState.errors.name?.message}
            />

            <Box>
              <Text
                as="label"
                htmlFor="card-number-element"
                className="block mb-1"
              >
                Card number
              </Text>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none z-0">
                  <IconCreditCard size={16} />
                </span>
                <div
                  id="card-number-element"
                  className="w-full h-10 pl-10 border border-[var(--gray-6)] rounded-md relative z-10"
                />
              </div>
            </Box>

            <Flex gap="3">
              <Box className="flex-1">
                <Text
                  as="label"
                  htmlFor="card-expiry-element"
                  className="block mb-1"
                >
                  Expiry date
                </Text>
                <div
                  id="card-expiry-element"
                  className="w-full h-10 px-3 border border-[var(--gray-6)] rounded-md relative z-10"
                />
              </Box>

              <Box className="flex-1">
                <Text
                  as="label"
                  htmlFor="card-cvv-element"
                  className="block mb-1"
                >
                  Security code
                </Text>
                <div
                  id="card-cvv-element"
                  className="w-full h-10 px-3 border border-[var(--gray-6)] rounded-md relative z-10"
                />
              </Box>
            </Flex>

            <FormInput
              {...form.register('postalCode')}
              name="postalCode"
              label="Postal Code"
              placeholder="92614"
              required
              error={
                form.formState.errors.postalCode?.message ??
                (!form.watch('postalCode')
                  ? 'Enter the card billing ZIP code.'
                  : undefined)
              }
            />

            {paymentError && (
              <Card className="mt-2 bg-red-50 border-red-200">
                <Flex gap="2" align="center">
                  <IconAlertCircle color="red" />
                  <Text color="red">{paymentError}</Text>
                </Flex>
              </Card>
            )}
          </Flex>
        </div>
      </Box>
    );
  },
);

PaymentFormInner.displayName = 'PaymentFormInner';

// Wrapper component that checks payfacId before rendering the form
const PaymentForm = forwardRef<PaymentFormHandle, Props>((props, ref) => {
  if (!props.organization.payfacId) {
    return <PaymentFormUnavailable />;
  }

  return (
    <PaymentFormInner
      {...props}
      ref={ref}
      organization={{
        ...props.organization,
        payfacId: props.organization.payfacId,
      }}
    />
  );
});

PaymentForm.displayName = 'PaymentForm';

export default PaymentForm;
